import { Controller, useFieldArray, useForm, useFormContext } from "react-hook-form";
import { defaultReturnRequest, ReturnRequest, returnRequestFields, returnRequestSchema } from "./types.ts";
import DeleteIcon from "@mui/icons-material/Delete";
import { styles } from "../../contracts/estimateStyle.tsx";
import { Box, IconButton, Stack, TextField } from "@mui/material";
import { Button } from "reactstrap";
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { SortableRow } from "../../layouts/SortableRow.tsx";
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useReturnRequestApi } from "../../hooks/useReturnRequestApi.ts";
import { usePopup } from "../../hooks/usePopup.ts";
import ConsoleHeader from "../../components/common/ConsoleHeader.tsx";
import { designTokens } from "../../theme.ts";

export default function ReturnRequestForm() {
    const method = useForm<ReturnRequest>({
        resolver: zodResolver(returnRequestSchema),
        defaultValues: {
            ...defaultReturnRequest,
            id: crypto.randomUUID(),
        },
        mode: "onBlur",
    });
    const { control, handleSubmit, trigger, formState: { errors } } = method
    const [loading, setLoading] = useState(false);
    
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
        );
    const { insertReturnRequest } = useReturnRequestApi();

    // remark 배열용
    const {
        fields: remarkList,
        append: appendRemark,
        remove: removeRemark,
        move: moveRemark,
    } = useFieldArray<ReturnRequest, "memo">({
        control,
        name: "memo",
    });

    const addRemark = async () => appendRemark("")

    // � 드래그 종료 시 배열 순서 업데이트
    const handleDragEndRemark = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = remarkList.findIndex(f => f.id === active.id);
        const newIndex = remarkList.findIndex(f => f.id === over.id);
        moveRemark(oldIndex, newIndex);          // useFieldArray 의 move
    };

    const handleInsertReturnRequest = async () => {
        const isValid = await trigger();
        if (isValid){
            try {
                const data = method.getValues()
                insertReturnRequest(data)
                setLoading(true)
            } catch (error) {
                console.log("Fail to insert data")
            }
        } else {
            console.log("There are errors in the form")
        }       
    };

    // React 예시
    const { openDownloadPopup } = usePopup();

    const onDownloadPopup = () => {
        const tempData = method.getValues();
        openDownloadPopup({
            path: "/popup-download/return-request",
            key: `${tempData.id}`,
            payloadData: tempData,
            windowName: "회수요청서 파일 다운로드"
        });
    };

    return (
        <Box sx={{ fontFamily: designTokens.fonts.sans }}>
            <ConsoleHeader
                code="MARS · RETURN · INTAKE"
                title="회수요청서 작성"
                meta="회수할 항목을 입력한 뒤 제출하세요."
            />
            <form style={styles.form}>
            <Stack spacing={4}>
                <div style={styles.grid}>
                <label
                    key={"returnTime"}
                    style={{
                        ...styles.label,
                        ...(false ? styles.fullWidth : null),
                    }}
                    >
                {"회수일시"}
                {true && <span style={styles.required}>*</span>}
                <Controller
                    name="returnTime"
                    control={control}
                    render={({ field }) => (
                        <>
                        <input
                        {...field}
                        type="datetime-local"   // 여기서 날짜+시간 입력 가능
                        style={styles.input}
                        step={60}               // 초 단위 step → 60초 = 1분 단위로 선택
                        />
                        {errors.returnTime && (
                            <span style={{ color: "red" }}>
                            {errors.returnTime.message}
                            </span>
                        )}
                        </>
                    )}
                    />
                </label>

                {returnRequestFields.map(({ label, name, type, required, multiline }) => {
                    // 현재 필드(name)에 해당하는 에러만 꺼내기
                    const fieldError = (errors as any)[name as keyof ReturnRequest];

                    return (
                        <label
                            key={name}
                            style={{
                                ...styles.label,
                                ...(multiline ? styles.fullWidth : null),
                            }}
                        >
                            {label}
                            {required && <span style={styles.required}>*</span>}

                            <Controller
                                name={name as keyof ReturnRequest}
                                control={control}
                                rules={required ? { required: true } : undefined}
                                render={({ field }) => (
                                    <>
                                        <input
                                            {...field}
                                            type={type}
                                            style={styles.input}
                                        />
                                        {fieldError && (
                                            <span style={{ color: "red" }}>
                                                {fieldError.message ?? "필수 입력 항목입니다."}
                                            </span>
                                        )}
                                    </>
                                )}
                            />
                        </label>
                    );
                })}

                </div>
            </Stack>

            <label
                key={"memo"}
                style={{
                    ...styles.label,
                    ...(false ? styles.fullWidth : null),
                }}
                >
                {"메모"}
                
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEndRemark}
                >
                    <SortableContext
                        items={remarkList.map(f => f.id)}          // 반드시 고유 id 배열
                        strategy={verticalListSortingStrategy}
                    >
                    {remarkList.map((item, index) => (
                        <SortableRow key={item.id} id={item.id}>
                            <Stack key={item.id} direction="row" spacing={2} alignItems="flex-start">
                                {/* ── 설명 ── */}
                                <Controller
                                    name={`memo.${index}`}
                                    control={control}
                                    render={({ field }) => (
                                    <TextField {...field} 
                                        label="기타사항"
                                        defaultValue={item[index]}
                                        sx={{ minWidth: 1080 }} />
                                    )}
                                />

                                {/* 삭제 버튼 */}
                                <IconButton onClick={() => removeRemark(index)}>
                                    <DeleteIcon />
                                </IconButton>
                            </Stack>
                        </SortableRow>
                    ))}
                    </SortableContext>
                </DndContext>
                <Button onClick={ addRemark }>
                    + 기타사항 행 추가
                </Button>
            </label>
            
            </form>

            <div 
            className="space-x-2" 
            style={{
            display: "flex",
            justifyContent: "flex-end", // ① 오른쪽 끝으로 밀기
            gap: "0.5rem",              // ② 버튼 간 간격(기존 space-x-2 대체)
            }}
            >
                <Button onClick={handleInsertReturnRequest} style={styles.button}>
                    저장
                </Button>

                <Button onClick={onDownloadPopup}
                   disabled={!loading}
                   style={styles.button}
                >
                    파일 다운로드
                </Button>
            </div>
        </Box>
    )


}
